import { zodResolver } from '@hookform/resolvers/zod';
import { Grid, Typography } from '@mui/material';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import {
  EditableLegalClientSchema,
  IEditableLegalClient,
} from '../../../../../Interfaces/IEditLegalClient';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { getEmptyNaturalPerson } from '../../../../../Interfaces/INaturalPerson';
import { useAlert } from '../../../../core/AlertContext';

import { CustomeSlide } from '../../../../core/CustomeSlide';
import { useDialogContext } from '../../../../core/DialogPresenter/DialogContext';
import { useRegisterDialogPage } from '../../../../core/DialogPresenter/useRegisterDialogPage';
import { TableLegalClient } from '../../../../LegalClient/TableLegalClient';
import { useLegalClientStore } from '../../generic/legalClientStore';
import { NavRow } from '../../generic/NavRow';
import {
  isFamilyRelationship,
  isMarriageRelationship,
} from '../../Inherit/utilities';
import NewFamilyTree from '../components/NewFamilyTree/NewFamilyTree';
import {
  useFamilyTreeInteractionContext,
  withFamilyTreeInteractionContext,
} from '../components/NewFamilyTree/NewFamilyTreeInteractionContext';

export type ITestamentRelativesProps = {
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
};

function TestamentRelativesSlide({
  relationshipArray,
  setRelationshipArray,
}: ITestamentRelativesProps) {
  const id = useId();
  const { handleNext, handleBack } = useDialogContext();
  const { showAlert } = useAlert();
  const { openEditLegalClientDialog } = useFamilyTreeInteractionContext();

  const pageNumber = useRegisterDialogPage(id, {
    title: 'Angehörige',
  });

  const [legalClientArray] = useLegalClientStore(state => [
    state.legalClientArray,
  ]);

  const onClickEdit = (legalClient: ILegalClient) => {
    openEditLegalClientDialog(legalClient.idLegalClient);
  };

  const localHandleNext = () => {
    const relativesRelationships = relationshipArray.filter(
      rel => isFamilyRelationship(rel) || isMarriageRelationship(rel)
    );

    if (relativesRelationships.length === 0) {
      showAlert({
        severity: 'warning',
        text: 'Bitte fügen Sie mindestens eine verwandte Person oder Ehepartner hinzu, um fortzufahren.',
      });
      return;
    }

    handleNext();
  };

  const methods = useForm<IEditableLegalClient>({
    mode: 'onChange',
    defaultValues: {
      ...getEmptyLegalClient(),
      ...getEmptyNaturalPerson(),
    },
    resolver: zodResolver(EditableLegalClientSchema),
  });

  return pageNumber === null ? null : (
    <CustomeSlide key={id} pageNumber={pageNumber} appear={false}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='h4' sx={{ mb: 3 }}>
            Angehörige
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            In diesem Abschnitt können Sie die Verwandten des Erblassers
            angeben. Diese Informationen sind wichtig für die rechtliche
            Gültigkeit des Testaments.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <NewFamilyTree
            relationshipArray={relationshipArray}
            setRelationshipArray={setRelationshipArray}
          />
        </Grid>

        <Grid item xs={12}>
          <TableLegalClient
            legalClientArray={legalClientArray}
            onRowClick={onClickEdit}
            title='Angehörige'
          />
        </Grid>

        <NavRow handleBack={handleBack} handleNext={localHandleNext} />
      </Grid>
    </CustomeSlide>
  );
}

export const TestamentRelatives = withFamilyTreeInteractionContext(
  TestamentRelativesSlide
);
